class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    document.dispatchEvent(new CustomEvent('cart-drawer:opening'));
    document.body.classList.add('cart-drawer-open');
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('cart-drawer-open');
    document.body.classList.remove('overflow-hidden');
    document.dispatchEvent(new CustomEvent('cart-drawer:closed'));
    document.querySelectorAll('[data-cart-pending="true"]').forEach((button) => {
      button.disabled = false;
      button.removeAttribute('aria-disabled');
      button.removeAttribute('aria-busy');
      button.classList.remove('loading', 'is-loading');
      button.removeAttribute('data-cart-pending');
    });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

window.refreshAndOpenCartDrawer = async function (triggeredBy) {
  const cartDrawer = document.querySelector('cart-drawer');
  if (!cartDrawer) return false;

  const root = window.Shopify?.routes?.root || '/';
  const response = await fetch(`${root}?sections=cart-drawer,cart-icon-bubble`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error('Unable to refresh cart');

  const sections = await response.json();
  cartDrawer.setActiveElement(triggeredBy || document.activeElement);
  cartDrawer.renderContents({ id: null, sections });
  return true;
};

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartAccessorySlider extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    this.addEventListener('change', this.handleVariantChange.bind(this));
    this.addEventListener('click', this.handleAdd.bind(this));
    this.addEventListener('click', this.handleNavigation.bind(this));
  }

  handleNavigation(event) {
    const arrow = event.target.closest('[data-accessory-previous], [data-accessory-next]');
    if (!arrow || !this.contains(arrow)) return;
    const rail = this.querySelector('.cart-accessories__rail');
    const card = rail?.querySelector('.cart-accessories__card');
    if (!rail || !card) return;

    const gap = Number.parseFloat(window.getComputedStyle(rail).gap) || 0;
    const direction = arrow.hasAttribute('data-accessory-previous') ? -1 : 1;
    rail.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: 'smooth' });
  }

  handleVariantChange(event) {
    const select = event.target.closest('[data-accessory-select]');
    if (!select || select.tagName !== 'SELECT') return;
    const card = select.closest('.cart-accessories__card');
    const option = select.options[select.selectedIndex];
    const image = card?.querySelector('.cart-accessories__image');
    const price = card?.querySelector('[data-accessory-price]');
    const comparePrice = card?.querySelector('[data-accessory-compare]');
    if (image && option.dataset.image) image.src = option.dataset.image;
    if (price && option.dataset.price) price.textContent = option.dataset.price;
    if (comparePrice) {
      comparePrice.textContent = option.dataset.comparePrice || '';
      comparePrice.hidden = !option.dataset.comparePrice;
    }
    card?.querySelector('.cart-accessories__price')?.classList.toggle('is-on-sale', Boolean(option.dataset.comparePrice));
  }

  async handleAdd(event) {
    const button = event.target.closest('[data-accessory-add]');
    if (!button || !this.contains(button)) return;
    const card = button.closest('.cart-accessories__card');
    const selector = card?.querySelector('[data-accessory-select]');
    const status = this.querySelector('[data-accessory-status]');
    if (!selector?.value) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (status) status.textContent = 'Wird hinzugefügt…';
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: selector.value, quantity: 1 }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'Produkt konnte nicht hinzugefügt werden.');
      if (status) status.textContent = 'Zum Warenkorb hinzugefügt';
      await window.refreshAndOpenCartDrawer?.(button);
    } catch (error) {
      if (status) status.textContent = error.message || 'Produkt konnte nicht hinzugefügt werden.';
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('cart-accessory-slider')) {
  customElements.define('cart-accessory-slider', CartAccessorySlider);
}
